import { decimal, stopDistances, type StopInput } from "@auto-pools/core";
import { formatFundingUsd, formatPrice } from "./format";
import type { StopDistanceRowView } from "../components/StopDistanceControl";

/** Shown under a row whose Custom text does not pass `isStopDistance`. */
export const INVALID_STOP_DISTANCE_REASON =
  "Enter a distance from -50% to 50% with at most one decimal place.";

/** One decimal, a space before "%": the distance format every stop row shares ("5" -> "5.0 %"). */
export function formatStopDistance(value: string): string {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? `${numeric.toFixed(1)} %` : "—";
}

/**
 * Signed, one decimal, a real minus sign, no space before "%": the "from the current price"
 * sub-line format. `value` is a signed percent (e.g. "-19.2345"); never rounds toward a false zero.
 */
export function formatStopFromCurrent(value: string): string {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  const magnitude = Math.abs(numeric).toFixed(1);
  if (Number(magnitude) === 0) return `${magnitude}%`;
  return `${numeric < 0 ? "−" : "+"}${magnitude}%`;
}

/**
 * A trigger's read-out row: formatted price, its USD value at a given quote price (null when
 * unpriced), and the signed "from the current price" text (null when the current price is
 * unavailable). `trigger` is undefined while no preview trigger price exists yet.
 */
export function buildStopRowView(
  trigger: string | undefined,
  referencePrice: string,
  priceUsd: string | null,
): StopDistanceRowView {
  if (trigger === undefined) return { price: undefined, usd: null, fromCurrent: null };
  let usd: string | null = null;
  if (priceUsd !== null) {
    try {
      usd = formatFundingUsd(decimal(trigger).mul(decimal(priceUsd)).toString());
    } catch {
      usd = null;
    }
  }
  let fromCurrent: string | null = null;
  try {
    if (referencePrice && decimal(referencePrice).units > 0n) {
      const percent = decimal(trigger)
        .sub(decimal(referencePrice))
        .mul(decimal("100"))
        .div(decimal(referencePrice))
        .toString();
      fromCurrent = formatStopFromCurrent(percent);
    }
  } catch {
    fromCurrent = null;
  }
  return { price: formatPrice(trigger), usd, fromCurrent };
}

export interface StopPrefill {
  below: string;
  above: string;
  mirror: boolean;
  /** One-line notice for a one-sided legacy document; null for a per-side or legacy-both pair. */
  notice: string | null;
}

/**
 * Maps a resolved canonical {lower, upper} distance pair into the display frame, choosing the
 * mirror flag and, for a one-sided legacy document, the "side is being added" notice. Returns null
 * when neither bound carries a distance — callers decide what "no stop at all" means for their own
 * state (the wizard's disabled-prefill default, the dialog's "no stop saved"/"no stop in force"
 * defaults) rather than this shared mapping inventing one.
 */
export function mapStopPrefill(
  distances: { lower: string | null; upper: string | null },
  inverted: boolean,
): StopPrefill | null {
  const below = inverted ? distances.upper : distances.lower;
  const above = inverted ? distances.lower : distances.upper;
  if (below === null && above === null) return null;
  if (below !== null && above !== null)
    return { below, above, mirror: Number(below) === Number(above), notice: null };
  const armed = below !== null ? "lower" : "upper";
  const missing = armed === "lower" ? "upper" : "lower";
  const value = below ?? above ?? "0";
  return {
    below: value,
    above: value,
    mirror: true,
    notice: `The previous setup guarded only the ${armed} side; the ${missing} stop is now added at the same distance.`,
  };
}

function formatDistancePair(below: string | null, above: string | null): string {
  if (below !== null && above !== null)
    return Number(below) === Number(above)
      ? `both sides · ${formatStopDistance(below)}`
      : `${formatStopDistance(below)} below · ${formatStopDistance(above)} above`;
  if (below !== null) return `below · ${formatStopDistance(below)}`;
  if (above !== null) return `above · ${formatStopDistance(above)}`;
  return "—";
}

/**
 * A one-line summary of the per-side stop distances, from core `stopDistances`. Both sides equal
 * (a legacy-both document or an equal per-side pair) read as "both sides"; unequal per-side reads as
 * a pair; a one-sided legacy document keeps its existing "below · X %" wording. Canonical lower is
 * always "below" and canonical upper always "above" — the existing, frame-invariant convention this
 * app already uses for `StopSide`. Never throws: a malformed stored document reads as "—" so a card
 * never fails to render on it.
 */
export function stopPolicyText(policy: StopInput): string {
  try {
    const { lower, upper } = stopDistances(policy);
    return formatDistancePair(lower, upper);
  } catch {
    return "—";
  }
}

/**
 * Same wording as {@link stopPolicyText}, for a caller that already resolved the pair itself — the
 * wizard's review step, which must map a CANONICAL quote policy into the DISPLAY frame before
 * naming a side, so it cannot hand this function the raw policy.
 */
export function stopPairText(pair: { below: string | null; above: string | null }): string {
  return formatDistancePair(pair.below, pair.above);
}
